/**
 * Componente de preview de resposta (reply)
 * 
 * Exibe a mensagem que está sendo respondida acima do campo de input,
 * com opção de cancelar a resposta. Estilo similar ao WhatsApp Web.
 */

import { X, Image, Mic, FileText, Video, MapPin, Contact } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Message } from '@/pages/tenant/Chat';
import { cn } from '@/lib/utils';

interface ReplyPreviewProps {
  /** Mensagem que está sendo respondida */
  message: Message;
  /** Callback para cancelar a resposta */
  onCancel: () => void;
}

/**
 * Retorna preview textual baseado no tipo de mensagem
 */
function getMessagePreview(message: Message): { icon: React.ReactNode; text: string } {
  switch (message.message_type) {
    case 'image':
      return {
        icon: <Image className="h-3 w-3" />,
        text: message.content || 'Foto',
      };
    case 'audio':
      return {
        icon: <Mic className="h-3 w-3" />,
        text: 'Mensagem de voz',
      };
    case 'video':
      return {
        icon: <Video className="h-3 w-3" />,
        text: message.content || 'Vídeo',
      };
    case 'document':
      return {
        icon: <FileText className="h-3 w-3" />,
        text: message.content || 'Documento',
      };
    case 'location':
      return {
        icon: <MapPin className="h-3 w-3" />,
        text: message.content || 'Localização',
      };
    case 'contact':
      return {
        icon: <Contact className="h-3 w-3" />,
        text: message.content || 'Contato',
      };
    default:
      return {
        icon: null,
        text: message.content || '',
      };
  }
}

export function ReplyPreview({ message, onCancel }: ReplyPreviewProps) {
  const preview = getMessagePreview(message);
  const senderLabel = message.direction === 'outbound' ? 'Você' : 'Contato';

  return (
    <div className="flex items-center gap-2 p-2 mx-4 mb-2 bg-muted/50 rounded-lg border-l-4 border-primary overflow-hidden">
      {/* Conteúdo da mensagem citada */}
      <div className="flex-1 min-w-0">
        {/* Nome do remetente */}
        <p className="text-xs font-medium text-primary">
          {senderLabel}
        </p>
        {/* Preview do conteúdo */}
        {/* Preview do conteúdo - min-w-0 e overflow-hidden garantem truncate em flex */}
        <div className="flex items-center gap-1 text-sm text-muted-foreground min-w-0 overflow-hidden">
          {preview.icon}
          <span className="truncate">{preview.text}</span>
        </div>
      </div>

      {/* Botão de cancelar */}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        onClick={onCancel}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
